import React, {useMemo} from "react";
import FlexBox from "view/components/Layout/FlexBox";
import type {IGridSchemaCell} from "ts/api/grid-schema";
import {CellType} from "view/components/GridResponseBlock/types";
import {grey} from "@material-ui/core/colors";
import clsx from "clsx";
import FilesInput from "view/components/GridResponseBlock/CustomizeTable/Components/Rows/Cell/FilesInput";
import FilesResponseInput from "view/components/GridResponseBlock/CustomizeTable/Components/Rows/Cell/FilesResponseInput";
import {
  getCurrentCellError,
  isGridFileUploadError,
} from "view/components/TasksList/components/Modals/ResponseModal/forms/GridForm/helpers";
import {useTheme} from "@material-ui/core";
import useStyles from "./styles";
import TextInput from "./TextInput";
import OptionsInput from "./OptionsInput";
import getActivityDirections from "../getActivityDirections";
import {ActiveCells, border, ErrorCell} from "../../../tools";
import OptionsResponseInput from "./OptionsResponseInput";
import TextResponseInput from "./TextResponseInput";

type CellProps = {
  cell: IGridSchemaCell;
  rowIndex: number;
  columnIndex: number;
  onClickSettings: () => void;
  onClickMandatory: () => void;
  activeCells?: ActiveCells;
  includeRowTitles?: boolean;
  viewOnly?: boolean;
  isJustSavedDraft?: React.MutableRefObject<boolean>;
  isResponseMode?: boolean;
  openParagraph?: (rowNumber: number, columnNumber: number) => void;
  errorCells?: ErrorCell[];
};

const greyBorder = `1px solid ${grey[300]}`;

function Cell({
  cell,
  rowIndex,
  columnIndex,
  onClickSettings,
  onClickMandatory,
  activeCells,
  includeRowTitles,
  isJustSavedDraft,
  viewOnly,
  isResponseMode,
  openParagraph,
  errorCells,
}: CellProps) {
  const classes = useStyles();
  const theme = useTheme();
  const currCellErrorType = useMemo(
    () => getCurrentCellError(rowIndex, columnIndex, errorCells),
    [rowIndex, columnIndex, errorCells]
  );
  const activityDirections = useMemo(
    () => getActivityDirections(rowIndex, columnIndex, activeCells),
    [rowIndex, columnIndex, activeCells]
  );
  const borderStyle = `1px solid ${
    currCellErrorType === "maxFiles" && isResponseMode
      ? theme.palette.warning.main
      : isGridFileUploadError(currCellErrorType) && isResponseMode
      ? theme.palette.error.main
      : grey["300"]
  }`;
  const isAboveActive = activityDirections.includes("up");
  const isLeftToActive = activityDirections.includes("left");
  const isInActivityCenter = activityDirections.includes("center");

  const isAnyCenterActivity =
    activityDirections.includes("same") || isInActivityCenter;

  const borderLeft =
    includeRowTitles || columnIndex !== 0
      ? undefined
      : isAnyCenterActivity
      ? border()
      : greyBorder;
  const borderStyles = currCellErrorType
    ? {border: borderStyle}
    : {
        borderBottom:
          isAnyCenterActivity || isAboveActive ? border() : undefined,
        borderRight:
          isAnyCenterActivity || isLeftToActive ? border() : undefined,
        borderLeft,
      };

  return (
    <FlexBox
      gap={1}
      style={borderStyles}
      className={clsx(
        classes.cell,
        isResponseMode && cell.type === "text" && classes.cellResponse
      )}
      alignCenter={cell.type !== CellType.Files && !isResponseMode}
    >
      <CellContent
        isResponseMode={isResponseMode}
        cell={cell}
        viewOnly={viewOnly}
        rowIndex={rowIndex}
        columnIndex={columnIndex}
        openParagraph={openParagraph}
        onClickMandatory={onClickMandatory}
        onClickSettings={onClickSettings}
        isJustSavedDraft={isJustSavedDraft}
        isAnyCenterActivity={isAnyCenterActivity}
      />
    </FlexBox>
  );
}

type CellContentProps = Omit<
  CellProps,
  "includeRowTitles" | "activeCells" | "errorCells"
> & {
  isAnyCenterActivity: boolean;
};

function CellContent({
  isResponseMode,
  cell,
  viewOnly,
  rowIndex,
  columnIndex,
  openParagraph,
  onClickMandatory,
  onClickSettings,
  isJustSavedDraft,
  isAnyCenterActivity,
}: CellContentProps) {
  if (isResponseMode) {
    const responseInputProps = {
      isJustSavedDraft,
      isRequired: !!cell.required,
      viewOnly,
      rowIndex,
      columnIndex,
    };
    if (cell.type === CellType.Options) {
      return (
        <OptionsResponseInput
          {...responseInputProps}
          options={cell.options ?? []}
        />
      );
    }
    if (cell.type === CellType.Files) {
      const {type, ...rest} = cell;
      return <FilesResponseInput {...responseInputProps} {...rest} />;
    }
    return (
      <TextResponseInput
        isJustSavedDraft={isJustSavedDraft}
        isRequired={!!cell.required}
        viewOnly={viewOnly}
        rowIndex={rowIndex}
        columnIndex={columnIndex}
        openParagraph={openParagraph}
      />
    );
  }
  const cellInputProps = {
    viewOnly,
    isActive: isAnyCenterActivity,
    isRequired: !!cell.required,
    onClickMandatory,
    onClickSettings,
  };
  if (cell.type === CellType.Files) {
    return <FilesInput {...cellInputProps} />;
  }
  if (cell.type === CellType.Options) {
    return <OptionsInput {...cellInputProps} />;
  }
  return <TextInput {...cellInputProps} />;
}

export default Cell;
